import React, { useEffect, useState } from "react";
import { errorMessage, isCancellation } from "@streamoid/errors";
import svgPaths from "../../imports/svg-ekgg6a0sqq";
import svgPathsBilling from "../../imports/svg-otljrqymr";
import "./style.css";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import FreePlan from "./FreePlanHeader";
import { useNavigate, useParams } from "react-router-dom";
import { apiCall } from "../../utils/api";
import { useSelector } from "react-redux";
import {
  formatDateToHumanReadable,
  notify,
  uploadFileToCloudfare,
} from "../../utils/utils";
import {
  fetchAllUserNames,
  fetchWorkspaceUserNames,
  getDisplayName,
  type UserNameMap,
} from "../../utils/userNames";
import Modal from "../Modal";
import ChangeSubscriptionModal from "../Billing2/ChangeSubscriptionModal";
import CancelDowngrade from "./CancelDowngrade";
import {
  BILLING_DETAILS_UPDATE_API,
  CENTRALISED_BILLING_API,
  STRIPE_SUBSCRIPTION_PAGE_URL,
} from "../../utils/envConfig";
import CustomCreditModal from "./CustomCreditModal";
import { set } from "date-fns";
import { getWorkspaceDetails } from "../../utils/workspaces";
import InvoiceUploadModal from "./InvoiceUploadModal";
import BillingDetailsModal from "./BillingDetailsModal";
import TopupModal from "./TopupModal";
import {
  buildProductsUrl,
  isMissingBillingWorkspace,
  getCreditUsageHistory,
  getWorkspaceBillingCurrency,
  getRollOverList,
} from "../../utils/billing";

interface InvoiceData {
  date: string;
  createdAt?: string;
  orderNumber: string;
  plan: string;
  creditAmount: string;
  amountPaid: number;
  currency?: string;
  isOfflineInvoice?: boolean;
  offlineUploaded?: boolean;
  meta: any;
  creditBlockId?: string;
  triggeredByUserId?: string;
  paymentPlatform?: string;
  expiresAt?: string;
  gracePeriodEndsAt?: string;
  transactionId?: string;
  rollover?: RolloverDetails;
}

interface RolloverDetails {
  rolloverId?: string;
  sourceAddBlockId?: string;
  rolloverAmount?: number;
  previousCycleStart?: string;
  previousCycleEnd?: string;
  currentCycleStart?: string;
  currentCycleEnd?: string;
  triggeredByUserId?: string;
  createdAt?: string;
}

interface CreditUsageData {
  id: string;
  type: string;
  triggeredByUserId: string;
  dateTime: string;
  action: string;
  credits: number | string;
  studio: string;
}

interface CreditUsagePagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

const CREDIT_USAGE_PAGE_SIZE = 100;

const formatUsageId = (id: string) => {
  if (!id || id.length <= 12) return id || "-";
  return `${id.slice(0, 8)}...${id.slice(-6)}`;
};

const formatCompactId = (id?: string) => {
  if (!id) return "-";
  if (id.length <= 18) return id;
  return `${id.slice(0, 10)}...${id.slice(-6)}`;
};

const getInvoiceMetaLabels = (meta: any) => {
  if (!meta || typeof meta !== "object") return [];

  const labels: string[] = [];
  if (meta.isTopUp) labels.push("Top-up");
  if (meta.isRollover) labels.push("Rollover");
  if (meta.isBonus) labels.push("Bonus");
  if (meta.stripeInvoiceId) labels.push("Stripe invoice");
  if (meta.currency) labels.push(String(meta.currency).toUpperCase());

  return labels;
};

const getCreditNoteFields = (meta: any) => {
  if (!meta || typeof meta !== "object") {
    return { note: "", requestedBy: "" };
  }

  // Grants written before the audit fields existed, and grants completed via a
  // stripe webhook, only carry them inside the metadata dump.
  const dump = meta.metadataDump || {};

  return {
    note: meta.note || dump.note || "",
    requestedBy: meta.requestedBy || dump.requestedBy || "",
  };
};

// A rolled-over block records only `rolloverId` / `sourceAddBlockId` in its
// meta; the amount and the cycles it moved between live on the rollover record,
// so the two are joined for display and the meta alone still renders something.
const buildRolloverDetails = (
  meta: any,
  rolloverRecord?: any,
): RolloverDetails | undefined => {
  const isRollover = Boolean(meta?.isRollover || rolloverRecord);

  if (!isRollover) return undefined;

  return {
    rolloverId: rolloverRecord?._id || meta?.rolloverId,
    sourceAddBlockId: rolloverRecord?.addBlockId || meta?.sourceAddBlockId,
    rolloverAmount: rolloverRecord?.rolloverAmount,
    previousCycleStart: rolloverRecord?.previousBillingCycle?.start,
    previousCycleEnd: rolloverRecord?.previousBillingCycle?.end,
    currentCycleStart: rolloverRecord?.currentBillingCycle?.start,
    currentCycleEnd: rolloverRecord?.currentBillingCycle?.end,
    triggeredByUserId: rolloverRecord?.triggeredByUserId,
    createdAt: rolloverRecord?.createdAt,
  };
};

const formatDetailDateTime = (value?: string) => {
  if (!value) return "-";
  return formatDateToHumanReadable(value, true);
};

const formatCycleRange = (start?: string, end?: string) => {
  if (!start && !end) return "-";
  return `${start ? formatDateToHumanReadable(start) : "-"} → ${
    end ? formatDateToHumanReadable(end) : "-"
  }`;
};

function DetailRow({
  label,
  value,
  wide = false,
}: {
  label: string;
  value: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={wide ? "sm:col-span-2 lg:col-span-3" : ""}>
      <div className="font-['Geist:Medium',_sans-serif] font-medium text-[11px] uppercase leading-[16px] tracking-[0.04em] text-[#84828e]">
        {label}
      </div>
      <div className="mt-1 font-['Geist:Regular',_sans-serif] text-[13px] leading-[20px] text-[#211f26] break-words">
        {value || "-"}
      </div>
    </div>
  );
}

function ExpandChevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`block size-4 shrink-0 transition-transform ${
        open ? "rotate-90" : ""
      }`}
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M9 6L15 12L9 18"
        stroke="#605d66"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

const getStatusPillClass = (status?: string) => {
  if (status === "active") return "bg-[#ecfdf3] text-[#027a48]";
  if (status === "trialing") return "bg-[#eff4ff] text-[#2d58ff]";
  if (status === "canceled" || status === "incomplete_expired") {
    return "bg-[#fef3f2] text-[#b42318]";
  }
  return "bg-[#f2eff3] text-[#5f5a66]";
};

const normalizeBillingCurrency = (currency?: string | null) => {
  return currency?.toUpperCase() === "INR" ? "INR" : "USD";
};

const getRegisteredBillingCurrency = (
  subscription?: any,
  customPlanDetails?: any,
) => {
  return normalizeBillingCurrency(
    customPlanDetails?.currency ||
      subscription?.currency ||
      subscription?.plan?.currency ||
      subscription?.plan?.metadata?.currency,
  );
};

// The display helper above defaults to USD because a price label always needs a
// symbol. Anything that picks a priceId must instead know when the currency is
// simply unknown, so it can widen the catalog rather than guess and offer a
// price Stripe will reject.
const resolveRegisteredBillingCurrency = (
  subscription?: any,
  customPlanDetails?: any,
): "usd" | "inr" | null => {
  const currency = String(
    customPlanDetails?.currency ||
      subscription?.currency ||
      subscription?.plan?.currency ||
      subscription?.plan?.metadata?.currency ||
      "",
  ).toLowerCase();

  return currency === "usd" || currency === "inr" ? currency : null;
};

const formatBillingAmount = (amount: number | string, currency?: string) => {
  const parsedAmount = Number(amount);

  if (Number.isNaN(parsedAmount)) {
    return "-";
  }

  const amountText = Number.isInteger(parsedAmount)
    ? String(parsedAmount)
    : parsedAmount.toFixed(2).replace(/\.?0+$/, "");
  const symbol = normalizeBillingCurrency(currency) === "INR" ? "₹" : "$";

  return `${symbol}${amountText}`;
};

/** The shared part of a pool member's balance: what is left after its own credits. */
const sharedCreditsLeft = (
  availableCredits: number,
  sharedPool: { ownCredits?: number; remaining?: number },
) =>
  typeof sharedPool.ownCredits === "number"
    ? Math.max(0, Number((availableCredits - Math.max(0, sharedPool.ownCredits)).toFixed(2)))
    : (sharedPool.remaining ?? 0);

function CreditsSummary({
  subscription,
  availableCredits,
  bonusAndTopUpCredits,
  bonusAndTopUpCreditsBlock,
  totalCredits,
  customPlanDetails,
  sharedPool,
}) {
  return (
    <div className="box-border flex flex-col gap-[8px] items-start justify-center p-0 relative  text-[#211f26] text-left text-nowrap w-full">
      <div className="flex flex-col font-['Geist:Medium',_sans-serif] font-medium justify-center relative  text-[16px]">
        <p className="block leading-[20px] text-nowrap whitespace-pre">
          Credits remaining
        </p>
      </div>
      <div className="flex flex-col font-['Geist:SemiBold',_sans-serif] font-semibold justify-center relative  text-[0px] tracking-[-0.64px] ">
        <p className="text-nowrap whitespace-pre">
          <span className="font-['Geist:SemiBold',_sans-serif] font-semibold leading-[48px] text-[40px] tracking-[-0.6px]">
            {availableCredits}
          </span>
          <span className="font-['Geist:SemiBold',_sans-serif] font-semibold leading-[48px] text-[40px] tracking-[-0.6px]">
            /{totalCredits}
          </span>
          {bonusAndTopUpCredits > 0 && (
            <span className="font-[400] leading-[48px] text-[18px] tracking-[-0.6px]">
              {" "}
              (Including Bonus: {bonusAndTopUpCredits})
            </span>
          )}
          {sharedPool && (
            <span className="font-[400] leading-[48px] text-[18px] tracking-[-0.6px]">
              {" "}
              (Including Shared: {sharedCreditsLeft(availableCredits, sharedPool)} from{" "}
              {sharedPool.sponsorName || "another workspace"})
            </span>
          )}
        </p>
      </div>
      {sharedPool && (
        <div className="header-grayed-out-text">
          Own credits are used first, then up to {sharedPool.allowance} shared credits this cycle
        </div>
      )}
      <div className="header-grayed-out-text">
        Credits reset every{" "}
        {subscription?.plan?.name?.toLowerCase() === "custom" &&
        customPlanDetails &&
        Object.keys(customPlanDetails).length > 0
          ? subscription.preDowngradeStripePriceData
            ? customPlanDetails?.downgradeData?.billingCycle === "monthly"
              ? "month"
              : "year"
            : customPlanDetails?.billingCycle === "monthly"
              ? "month"
              : "year"
          : "month"}
        {` (${
          subscription?.plan?.subscriptionEndDate
            ? formatDateToHumanReadable(subscription?.plan?.subscriptionEndDate)
            : "-"
        })`}
      </div>
    </div>
  );
}

function CreditsCard({
  subscription,
  availableCredits,
  totalCredits,
  customPlanDetails,
  bonusAndTopUpCredits,
  bonusAndTopUpCreditsBlock,
  sharedPool,
}) {
  return (
    <div
      className="bg-[rgba(253,252,253,0.64)] box-border content-stretch flex flex-row gap-10 items-center justify-start p-[16px] relative rounded-2xl self-stretch "
      data-name="App Card"
    >
      <div className="absolute border border-[#eae7ec] border-solid inset-0 pointer-events-none rounded-2xl" />
      <CreditsSummary
        availableCredits={availableCredits}
        bonusAndTopUpCredits={bonusAndTopUpCredits}
        bonusAndTopUpCreditsBlock={bonusAndTopUpCreditsBlock}
        totalCredits={totalCredits}
        customPlanDetails={customPlanDetails}
        subscription={subscription}
        sharedPool={sharedPool}
      />
      <div className="absolute inset-0 pointer-events-none shadow-[0px_1px_1px_1px_inset_rgba(255,255,255,0.12),0px_1px_1px_0px_inset_rgba(255,255,255,0.16)]" />
    </div>
  );
}

function PlanBadge({ planName }) {
  return (
    <div
      className="bg-[#cadfff] box-border content-stretch flex flex-row gap-2 items-center justify-center px-2 py-1 relative rounded-lg "
      data-name="Ticker %"
    >
      <div className="flex flex-col font-['Geist:Medium',_sans-serif] font-medium justify-center leading-[0] relative  text-[#211f26] text-[12px] text-center text-nowrap">
        <p className="block leading-[16px] whitespace-pre">{planName}</p>
      </div>
    </div>
  );
}

function PlanInfo({ planName }) {
  return (
    <div className="box-border content-stretch flex flex-row gap-1 items-center justify-start p-0 relative  w-full">
      <div className="flex flex-col font-['Geist:Medium',_sans-serif] font-medium justify-center leading-[0] relative  text-[#211f26] text-[16px] text-left text-nowrap">
        <p className="block leading-[20px] whitespace-pre">
          <span className="text-[#2D58FF]"> {planName} </span>
        </p>
      </div>
      <div className="flex flex-col font-['Geist:Medium',_sans-serif] font-medium justify-center leading-[0] relative  text-[#211f26] text-[16px] text-left text-nowrap">
        <p className="block leading-[20px] whitespace-pre">plan</p>
      </div>
    </div>
  );
}

function PlanHeader({ planName }) {
  return (
    <div
      className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative  w-full"
      data-name="Heading 5"
    >
      <PlanInfo planName={planName} />
    </div>
  );
}

const priceToShow = (subscription, customPlanDetails) => {
  const currency = getRegisteredBillingCurrency(subscription, customPlanDetails);

  // Case 1: Custom plan and we have pre-downgrade data → show custom plan upgrade price
  if (subscription.plan?.name?.toLowerCase() === "custom") {
    if (subscription && subscription.preDowngradeStripePriceData) {
      return formatBillingAmount(
        customPlanDetails?.downgradeData?.price,
        currency,
      );
    }

    // // Case 2: If downgrade data exists → show that price
    // if (customPlanDetails?.upgradeData?.price) {
    //   return `${currency} ${customPlanDetails.upgradeData.price}`;
    // }

    const baseAmount = subscription?.preDowngradeStripePriceData
      ? subscription.preDowngradeStripePriceData.amount
      : (subscription?.plan?.amount ?? 0);

    return formatBillingAmount(baseAmount / 100, currency);
  }

  if (
    subscription?.preDowngradeStripePriceData?.name?.toLowerCase() === "custom"
  ) {
    if (customPlanDetails?.downgradeData?.price) {
      return formatBillingAmount(customPlanDetails.downgradeData.price, currency);
    }

    const baseAmount = subscription?.preDowngradeStripePriceData
      ? subscription.preDowngradeStripePriceData.amount
      : (subscription?.plan?.amount ?? 0);

    return formatBillingAmount(baseAmount / 100, currency);
  }

  // Case 3: Otherwise fallback to pre-downgrade amount or subscription plan amount
  const baseAmount = subscription?.preDowngradeStripePriceData
    ? subscription.preDowngradeStripePriceData.amount
    : (subscription?.plan?.amount ?? 0);

  return formatBillingAmount(baseAmount / 100, currency);
};

const creditsToShow = (subscription, customPlanDetails) => {
  if (
    subscription.plan?.name?.toLowerCase() === "custom" ||
    subscription.preDowngradeStripePriceData?.name?.toLowerCase() === "custom"
  ) {
    if (subscription && subscription.preDowngradeStripePriceData) {
      return `${customPlanDetails?.downgradeData?.credits}`;
    }

    const credits = customPlanDetails?.credits ?? 0;

    return `${credits}`;
  } else {
    if (subscription && subscription.preDowngradeStripePriceData) {
      return `${subscription?.preDowngradeStripePriceData?.credits}`;
    }

    const credits = subscription?.credits ?? 0;
    return `${credits}`;
  }
};

function PlanDetails({ subscription, customPlanDetails }) {
  return (
    <div className="basis-0 box-border content-stretch flex flex-col gap-2 grow h-full items-start justify-center min-h-px min-w-px p-0 relative ">
      <PlanHeader
        planName={
          subscription &&
          subscription.downgradeScheduled &&
          subscription.preDowngradeStripePriceData
            ? subscription.preDowngradeStripePriceData.name
            : subscription.plan?.name
        }
      />

      <div className="flex flex-col font-['Geist:SemiBold',_sans-serif] font-semibold justify-center leading-[0] relative  text-[#211f26] text-[0px] text-left text-nowrap tracking-[-0.64px]">
        <p className="whitespace-pre">
          <span className="font-['Geist:SemiBold',_sans-serif] font-semibold leading-[48px] text-[40px] tracking-[-0.6px]">
            {priceToShow(subscription, customPlanDetails)}
          </span>
          <span className="adjustLetterSpacing font-['Geist:Medium',_sans-serif] font-medium leading-[20px] text-[#211f26] text-[16px]">
            {" "}
            billed{" "}
          </span>
          {subscription?.plan?.name?.toLowerCase() === "custom" &&
          Object.keys(customPlanDetails).length > 0 ? (
            <span className="adjustLetterSpacing font-['Geist:Medium',_sans-serif] font-medium leading-[20px] text-[#211f26] text-[16px]">
              {subscription.preDowngradeStripePriceData
                ? customPlanDetails?.downgradeData?.billingCycle
                : customPlanDetails?.billingCycle}
            </span>
          ) : (
            <span className="adjustLetterSpacing font-['Geist:Medium',_sans-serif] font-medium leading-[20px] text-[#211f26] text-[16px]">
              Monthly
            </span>
          )}
          <span className="adjustLetterSpacing font-['Geist:Medium',_sans-serif] font-medium leading-[20px] text-[#211f26] text-[16px]">
            {" "}
            ( Current Plan: {creditsToShow(
              subscription,
              customPlanDetails,
            )} / {priceToShow(subscription, customPlanDetails)} )
          </span>
        </p>
      </div>

      <div className="text-[#211F26]">
        {subscription && subscription.downgradeScheduled ? (
          <>
            Your new plan{" "}
            <span className="font-[500]">
              {" "}
              {subscription.plan.name.toLowerCase() === "custom"
                ? `${subscription.plan.name} of ( ${formatBillingAmount(
                    customPlanDetails?.price,
                    getRegisteredBillingCurrency(
                      subscription,
                      customPlanDetails,
                    ),
                  )} ${customPlanDetails?.billingCycle} ) and ${customPlanDetails?.credits}  credits `
                : subscription.plan.name}{" "}
            </span>{" "}
            will take effect from{" "}
            {subscription?.plan.subscriptionEndDate
              ? formatDateToHumanReadable(
                  subscription?.plan.subscriptionEndDate,
                )
              : "-"}
          </>
        ) : (
          <>
            Next payment:{" "}
            {subscription?.plan?.subscriptionEndDate
              ? formatDateToHumanReadable(
                  subscription?.plan?.subscriptionEndDate,
                )
              : "-"}
          </>
        )}
      </div>
    </div>
  );
}

function PlanSection({ subscription, customPlanDetails }) {
  return (
    <div className="basis-0 box-border content-stretch flex flex-row gap-4 grow items-center justify-start min-h-px min-w-px p-0 relative ">
      <div className="basis-0 flex flex-row grow items-center self-stretch ">
        <PlanDetails
          subscription={subscription}
          customPlanDetails={customPlanDetails}
        />
      </div>
    </div>
  );
}

function ManageSubscriptionButton({
  handleShowCustomCreditsModal,
  customPlanDetails,
  subscription,
  handleCancelDowngrade,
  handleCancelPlan,
  handleShowTopUpModal,
}: {
  handleShowCustomCreditsModal?: () => void;
  customPlanDetails: any;
  subscription: any;
  handleCancelDowngrade?: () => void;
  handleCancelPlan?: () => void;
  handleShowTopUpModal?: () => void;
}) {
  return (
    <div className="flex flex-col gap-[8px]">
      <div className="flex items-center gap-[12px]">
        {subscription && subscription.downgradeScheduled ? (
          <button
            onClick={() => {
              const c = window.confirm("Are you sure to cancel the downgrade");
              if (c) {
                if (handleCancelDowngrade) handleCancelDowngrade();
              }
            }}
            className="bg-[#f2eff3] box-border content-stretch flex flex-row gap-2 h-10 items-center justify-center overflow-clip p-[16px] relative rounded-2xl  cursor-pointer hover:bg-[#e8e5ea] transition-colors"
            data-name="Button base"
          >
            Cancel Downgrade
          </button>
        ) : (
          <button
            className="add-custom-credit"
            onClick={handleShowCustomCreditsModal}
          >
            {customPlanDetails ? "Manage Custom Plan" : "Add Custom Plan"}
          </button>
        )}

        {subscription &&
        !subscription.downgradeScheduled &&
        subscription?.plan?.name != "Free" ? (
          <button
            onClick={handleCancelPlan}
            className="bg-[#f2eff3] box-border content-stretch flex flex-row gap-2 h-10 items-center justify-center overflow-clip p-[16px] relative rounded-2xl  cursor-pointer hover:bg-[#e8e5ea] transition-colors"
            data-name="Button base"
          >
            Downgrade to Free
          </button>
        ) : (
          <></>
        )}

        {/* 
      <button
        onClick={() => {
          if (subscription.downgradeScheduled) {
            notify(
              "Cancel the requested downgrade request in order to update plan",
              "error"
            );
          } else {
            onClick();
          }
        }}
        className="bg-[#2d58ff] box-border content-stretch flex flex-row gap-2 h-10 items-center justify-center overflow-clip p-[16px] relative rounded-2xl  cursor-pointer  transition-colors"
        data-name="Button base"
      >
        <div className="font-['Geist:Medium',_sans-serif] font-medium leading-[0] relative  text-[#fff] text-[14px] text-center text-nowrap">
          <p className="block leading-[18px] whitespace-pre">Manage plan</p>
        </div>
      </button> */}
      </div>
      <div className="flex items-center gap-[12px] w-full">
        <button
          className="add-custom-credit w-full"
          style={{
            opacity: subscription?.plan?.name != "Free" ? 1 : 0.5,
            cursor:
              subscription?.plan?.name != "Free" ? "pointer" : "not-allowed",
          }}
          onClick={() => {
            if (subscription?.plan?.name != "Free" && handleShowTopUpModal) {
              handleShowTopUpModal();
            } else {
              notify("Top-up is available for paid plans only", "error");
            }
          }}
        >
          Topup
        </button>
      </div>
    </div>
  );
}

function ButtonWrapper({
  handleShowCustomCreditsModal,
  customPlanDetails,
  subscription,
  handleCancelDowngrade,
  handleCancelPlan,
  handleShowTopUpModal,
}: {
  handleShowCustomCreditsModal?: () => void;
  customPlanDetails: any;
  subscription: any;
  handleCancelDowngrade: () => void;
  handleCancelPlan: () => void;
  handleShowTopUpModal: () => void;
}) {
  return (
    <div
      className="box-border content-stretch flex flex-row items-start justify-start p-0 relative "
      data-name="Button_cmp"
    >
      <ManageSubscriptionButton
        handleShowCustomCreditsModal={handleShowCustomCreditsModal}
        customPlanDetails={customPlanDetails}
        subscription={subscription}
        handleCancelDowngrade={handleCancelDowngrade}
        handleCancelPlan={handleCancelPlan}
        handleShowTopUpModal={handleShowTopUpModal}
      />
      {/* {subscription && subscription.plan ? (
        (subscription.plan.name.toLowerCase() === "free" ||
          subscription.plan.amount == 0) &&
        !subscription.downgradeScheduled ? (
          <div
            className="box-border content-stretch flex flex-row items-start justify-start p-0 relative shrink-0"
            data-name="Button_cmp"
          >
            <div
              className="bg-[#2d58ff] box-border content-stretch flex flex-row gap-2 h-10 items-center justify-center overflow-clip p-[16px] relative rounded-2xl shrink-0"
              data-name="Button base"
            >
              <div
                className="font-['Geist:Medium',_sans-serif] font-medium leading-[0] relative shrink-0 text-[#fcfcfd] text-[14px] text-center text-nowrap cursor-pointer"
                onClick={onClick}
              >
                <p className="block leading-[18px] whitespace-pre">
                  Upgrade plan
                </p>
              </div>
            </div>
          </div>
        ) : (
        )
      ) : (
        <></>
      } */}
    </div>
  );
}

function SubscriptionCard({
  subscription,
  handleShowCustomCreditsModal,
  customPlanDetails,
  handleCancelDowngrade,
  handleCancelPlan,
  handleShowTopUpModal,
}) {
  return (
    <div
      className="bg-[rgba(253,252,253,0.64)] grow min-w-px relative rounded-2xl self-stretch"
      data-name="App Card"
    >
      <div className="absolute border border-[#eae7ec] border-solid inset-0 pointer-events-none rounded-2xl" />
      <div className="flex flex-row items-center relative size-full">
        <div className="box-border content-stretch flex flex-row items-center justify-between p-[16px] relative w-full h-[100%]">
          <PlanSection
            subscription={subscription}
            customPlanDetails={customPlanDetails}
          />

          <ButtonWrapper
            handleShowCustomCreditsModal={handleShowCustomCreditsModal}
            customPlanDetails={customPlanDetails}
            subscription={subscription}
            handleCancelDowngrade={handleCancelDowngrade}
            handleCancelPlan={handleCancelPlan}
            handleShowTopUpModal={handleShowTopUpModal}
          />
        </div>
      </div>
      <div className="absolute inset-0 pointer-events-none shadow-[0px_1px_1px_1px_inset_rgba(255,255,255,0.12),0px_1px_1px_0px_inset_rgba(255,255,255,0.16)]" />
    </div>
  );
}

function StatsCards({
  availableCredits,
  bonusAndTopUpCredits,
  bonusAndTopUpCreditsBlock,
  totalValidCredits,
  sharedPool,
  subscription,
  handleShowCustomCreditsModal,
  customPlanDetails,
  handleCancelDowngrade,
  handleCancelPlan,
  handleShowTopUpModal,
}) {
  return (
    <div className="box-border content-stretch flex flex-row gap-6 items-start justify-start p-0 relative  w-full">
      <CreditsCard
        subscription={subscription}
        availableCredits={availableCredits}
        bonusAndTopUpCredits={bonusAndTopUpCredits}
        bonusAndTopUpCreditsBlock={bonusAndTopUpCreditsBlock}
        // A shared-pool member's balance is its own credits plus its sponsor's up
        // to its allowance, so it is out of its plan's credits and the allowance.
        totalCredits={(totalValidCredits ?? 0) + (sharedPool?.allowance ?? 0)}
        customPlanDetails={customPlanDetails}
        sharedPool={sharedPool}
      />
      <SubscriptionCard
        subscription={subscription}
        handleShowCustomCreditsModal={handleShowCustomCreditsModal}
        customPlanDetails={customPlanDetails}
        handleCancelDowngrade={handleCancelDowngrade}
        handleCancelPlan={handleCancelPlan}
        handleShowTopUpModal={handleShowTopUpModal}
      />
    </div>
  );
}

interface TabButtonProps {
  active?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
}

function TabButton({ active = false, children, onClick }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`basis-0 ${
        active ? "bg-[#faf9fb]" : ""
      } grow h-full min-h-px min-w-px relative rounded-lg  cursor-pointer hover:bg-[#f5f3f6] transition-colors`}
      data-name="_segmented-control-item"
    >
      {active && (
        <div className="absolute border-[#eae7ec] border-[0.2px] border-solid inset-0 pointer-events-none rounded-lg" />
      )}
      <div className="flex flex-row items-center justify-center relative size-full">
        <div className="box-border content-stretch flex flex-row gap-2 items-center justify-center px-4 py-0 relative size-full">
          <div className="font-['Geist:Medium',_sans-serif] font-medium leading-[0] relative  text-[#211f26] text-[14px] text-left text-nowrap">
            {children}
          </div>
        </div>
      </div>
      {active && (
        <div className="absolute inset-0 pointer-events-none shadow-[0px_1px_1px_1px_inset_rgba(255,255,255,0.12),0px_1px_1px_0px_inset_rgba(255,255,255,0.16)]" />
      )}
    </button>
  );
}

function TabsControl({
  activeTab,
  onTabChange,
}: {
  activeTab: "invoices" | "creditUsage" | "subscriptions";
  onTabChange: (tab: "invoices" | "creditUsage" | "subscriptions") => void;
}) {
  return (
    <div
      className="bg-[#faf9fb] h-8 relative rounded-lg"
      data-name="Segmented Control"
    >
      <div className="box-border content-stretch flex flex-row h-8 items-center justify-center overflow-clip p-0 relative">
        <TabButton
          active={activeTab === "creditUsage"}
          onClick={() => onTabChange("creditUsage")}
        >
          <p className="block leading-[20px] whitespace-pre">Credit usage</p>
        </TabButton>
        <TabButton
          active={activeTab === "invoices"}
          onClick={() => onTabChange("invoices")}
        >
          <p className="block leading-[18px] whitespace-pre">Invoices</p>
        </TabButton>
        <TabButton
          active={activeTab === "subscriptions"}
          onClick={() => onTabChange("subscriptions")}
        >
          <p className="block leading-[18px] whitespace-pre">Subscriptions</p>
        </TabButton>
      </div>
      <div className="absolute border border-[#eae7ec] border-solid inset-[-1px] pointer-events-none rounded-[9px]" />
    </div>
  );
}

function TabsSection({
  activeTab,
  onTabChange,
  handleUploadInvoiceBtn,
  invoiceTableData,
}: {
  activeTab: "invoices" | "creditUsage" | "subscriptions";
  onTabChange: (tab: "invoices" | "creditUsage" | "subscriptions") => void;
  handleUploadInvoiceBtn: () => void;
  invoiceTableData: any[];
}) {
  const offlineInvoices = invoiceTableData.filter(
    (invoice) => invoice.isOfflineInvoice,
  );
  return (
    <div className="box-border content-stretch flex flex-row gap-4 items-start justify-between p-0 relative  w-full">
      <TabsControl activeTab={activeTab} onTabChange={onTabChange} />
      {activeTab === "invoices" && offlineInvoices.length > 0 && (
        <>
          <button
            className={`border-[#5f5b61] border-[0.2px] border-solid h-full min-h-px min-w-px relative rounded-lg  cursor-pointer hover:bg-[#f5f3f6] transition-colors px-[12px]`}
            data-name="_segmented-control-item"
            onClick={(e) => {
              handleUploadInvoiceBtn();
            }}
          >
            <p className="block leading-[20px] whitespace-pre">Upload PDF</p>
          </button>
        </>
      )}
    </div>
  );
}

function DownloadIcon({
  onClick,
}: {
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <button
      onClick={onClick}
      className="inline-flex size-9 items-center justify-center rounded-lg border border-[#eae7ec] bg-white text-[#211f26] transition-colors hover:bg-[#f5f3f6]"
      data-name="file Download"
      title="Download invoice"
    >
      <svg
        className="block size-5"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 24 24"
      >
        <g id="file Download">
          <path
            d={svgPaths.p38c27af0}
            id="Icon"
            stroke="var(--stroke-0, #211F26)"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
          />
        </g>
      </svg>
    </button>
  );
}

function InvoicesTable({
  workspaceId,
  invoicesData,
  subscription,
  customPlanDetails,
  userNames,
}: {
  workspaceId: string;
  invoicesData: InvoiceData[];
  subscription: any;
  customPlanDetails: any;
  userNames: UserNameMap;
}) {
  const [loading, setLoading] = useState<boolean>();
  const [invoiceData, setInvoiceData] = useState();
  const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);
  const registeredCurrency = getRegisteredBillingCurrency(
    subscription,
    customPlanDetails,
  );

  // invoicesData = invoicesData.filter(
  //   (invoice: any) => invoice && invoice.name.toLowerCase() != "free"
  // );

  if (invoicesData && invoicesData.length == 0) {
    return (
      <Frame5
        title="No Invoices Found"
        description=" Choose a plan to unlock invoicing and manage your projects with ease."
      ></Frame5>
    );
  }

  const getFileExtension = (url) => {
    const path = url.split("?")[0]; // Remove query params if any
    const extension = path.split(".").pop();
    return `.${extension}`;
  };

  const handleDownload = async (orderNumber: string) => {
    const userUploadedPdf = await fetch(
      `${CENTRALISED_BILLING_API}/invoice/${workspaceId}/${orderNumber}`,
    ).then((res) => res.json());

    if (
      userUploadedPdf &&
      userUploadedPdf.data &&
      userUploadedPdf.data.length > 0
    ) {
      const invoices = userUploadedPdf.data;
      console.log(invoices);
      const fileName = "invoice";
      for (const invoice of invoices) {
        const fileURL = invoice.invoiceUrl.replace(
          ".catalogix.ai",
          ".streamoid.com",
        );

        try {
          // Fetch the actual file
          const fileRes = await fetch(fileURL);
          if (!fileRes.ok) {
            notify(`Failed to download ${invoice.invoiceId}`, "error");
            continue;
          }

          const blob = await fileRes.blob();
          const url = window.URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = url;
          link.download =
            "invoice" + getFileExtension(fileURL) || "invoice.pdf";

          document.body.appendChild(link);
          link.click();
          link.remove();

          window.URL.revokeObjectURL(url);
        } catch (err) {
          notify(`Error fetching invoice file`, "error");
        }
      }
    } else {
      apiCall(
        `${CENTRALISED_BILLING_API}/subscriptions/invoices/${workspaceId}/${orderNumber}`,
      )
        .then(async (res: any) => {
          setLoading(false);
          console.log(res);
          if (res.data) {
            const invoiceData = res.data.invoiceData;

            if (invoiceData.id.startsWith("in_")) {
              const downloadUrl = invoiceData.invoice_pdf;
              window.open(downloadUrl, "_blank");
            }
            if (invoiceData.id.startsWith("pi_")) {
              const downloadUrl = res.data.receiptPdfUrl;

              const response = await fetch(downloadUrl);
              const blob = await response.blob();

              const url = window.URL.createObjectURL(blob);

              const a = document.createElement("a");
              a.href = url;
              a.download = `receipt_${orderNumber}.pdf`;

              document.body.appendChild(a);
              a.click();
              a.remove();

              window.URL.revokeObjectURL(url);
            }
          } else {
            notify("Unable to fetch the invoice", "error");
            window.close();
          }
        })
        .catch((err: any) => {
          if (!isCancellation(err)) notify(errorMessage(err, { method: "GET" }), "error");
          setLoading(false);
          window.close();
        });
    }
  };

  return (
    <div
      className="backdrop-blur-[20px] backdrop-filter bg-[rgba(253,252,253,0.88)] border border-[#eae7ec] relative rounded-2xl w-full overflow-hidden"
      data-name="Table"
    >
      <div className="billing-table-scroll max-h-[520px] w-full overflow-auto">
        <div className="min-w-[1200px]">
          <Table className="w-full table-fixed">
            <TableHeader className="sticky top-0 z-10">
              <TableRow className="border-b-0">
                <TableHead className="bg-[#faf9fb] h-11 px-3 py-4 border-b border-[#eae7ec] w-[44px]">
                  <span className="sr-only">Expand</span>
                </TableHead>
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[200px]">
                  <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]">
                    DATE &amp; TIME
                  </div>
                </TableHead>
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[210px]">
                  <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]">
                    ORDER
                  </div>
                </TableHead>
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[120px]">
                  <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]">
                    CREDITS
                  </div>
                </TableHead>
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[150px]">
                  <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]">
                    AMOUNT
                  </div>
                </TableHead>
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[190px]">
                  <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]">
                    TRIGGERED BY
                  </div>
                </TableHead>
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[190px]">
                  <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]">
                    DETAILS
                  </div>
                </TableHead>
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[90px]">
                  <span className="sr-only">Download</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoicesData.map((invoice: InvoiceData, index) => {
                const metaLabels = getInvoiceMetaLabels(invoice.meta);
                const canDownload =
                  !invoice.isOfflineInvoice ||
                  (invoice.isOfflineInvoice && invoice.offlineUploaded);
                const rowKey = `${invoice.orderNumber}-${index}`;
                const isExpanded = expandedRowKey === rowKey;
                const { note, requestedBy } = getCreditNoteFields(invoice.meta);

                return (
                  <React.Fragment key={rowKey}>
                    <TableRow
                      className={`h-[68px] cursor-pointer border-b border-[#faf9fb] hover:bg-[#faf9fb]/70 ${
                        isExpanded ? "bg-[#faf9fb]" : ""
                      }`}
                      onClick={() =>
                        setExpandedRowKey(isExpanded ? null : rowKey)
                      }
                      aria-expanded={isExpanded}
                    >
                      <TableCell className="px-3 py-4">
                        <ExpandChevron open={isExpanded} />
                      </TableCell>
                      <TableCell className="px-5 py-4">
                        <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[14px] leading-[18px]">
                          {invoice.date}
                        </div>
                      </TableCell>
                      <TableCell className="px-5 py-4">
                        <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[14px] leading-[18px] truncate">
                          {formatCompactId(invoice.orderNumber)}
                        </div>
                      </TableCell>
                      <TableCell className="px-5 py-4">
                        <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[14px] leading-[18px]">
                          {invoice.creditAmount}
                        </div>
                      </TableCell>
                      <TableCell className="px-5 py-4">
                        <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[14px] leading-[18px]">
                          {formatBillingAmount(
                            invoice.amountPaid,
                            invoice.currency || registeredCurrency,
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="px-5 py-4">
                        <div
                          className="font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[14px] leading-[18px] truncate"
                          title={invoice.triggeredByUserId || ""}
                        >
                          {getDisplayName(invoice.triggeredByUserId, userNames)}
                        </div>
                      </TableCell>
                      <TableCell className="px-5 py-4">
                        <div className="flex max-w-full flex-wrap gap-1.5">
                          {metaLabels.length > 0 ? (
                            metaLabels.map((label) => (
                              <span
                                key={label}
                                className="inline-flex h-7 items-center rounded-full bg-[#f2eff3] px-3 font-['Geist:Medium',_sans-serif] font-medium text-[#5f5a66] text-[13px] leading-[18px]"
                              >
                                {label}
                              </span>
                            ))
                          ) : (
                            <span className="font-['Geist:Regular',_sans-serif] text-[#84828e] text-[14px]">
                              -
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="px-5 py-4">
                        {canDownload ? (
                          <DownloadIcon
                            onClick={(event) => {
                              // The row itself toggles the detail panel, so the
                              // download must not be read as a row click.
                              event.stopPropagation();
                              handleDownload(invoice.orderNumber);
                            }}
                          />
                        ) : (
                          <span className="font-['Geist:Regular',_sans-serif] text-[#a7a3ad] text-[13px]">
                            -
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow className="border-b border-[#eae7ec] bg-[#faf9fb] hover:bg-[#faf9fb]">
                        <TableCell colSpan={8} className="px-5 py-5">
                          <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                            <DetailRow
                              label="Note"
                              value={note}
                              wide={Boolean(note && note.length > 80)}
                            />
                            <DetailRow label="Requested by" value={requestedBy} />
                            <DetailRow
                              label="Order / Invoice"
                              value={
                                <span className="break-all">
                                  {invoice.orderNumber || "-"}
                                </span>
                              }
                            />
                            <DetailRow
                              label="Date & time"
                              value={formatDetailDateTime(invoice.createdAt)}
                            />
                            <DetailRow
                              label="Credits added"
                              value={invoice.creditAmount}
                            />
                            <DetailRow
                              label="Amount paid"
                              value={formatBillingAmount(
                                invoice.amountPaid,
                                invoice.currency || registeredCurrency,
                              )}
                            />
                            <DetailRow
                              label="Payment platform"
                              value={invoice.paymentPlatform}
                            />
                            <DetailRow
                              label="Triggered by"
                              value={
                                invoice.triggeredByUserId ? (
                                  <>
                                    {getDisplayName(
                                      invoice.triggeredByUserId,
                                      userNames,
                                    )}
                                    <span className="mt-0.5 block break-all text-[11px] leading-[16px] text-[#84828e]">
                                      {invoice.triggeredByUserId}
                                    </span>
                                  </>
                                ) : (
                                  "-"
                                )
                              }
                            />
                            <DetailRow
                              label="Credit block id"
                              value={
                                <span className="break-all">
                                  {invoice.creditBlockId || "-"}
                                </span>
                              }
                            />
                            <DetailRow
                              label="Expires at"
                              value={formatDetailDateTime(invoice.expiresAt)}
                            />
                            <DetailRow
                              label="Grace period ends"
                              value={formatDetailDateTime(
                                invoice.gracePeriodEndsAt,
                              )}
                            />
                            <DetailRow
                              label="Transaction id"
                              value={
                                <span className="break-all">
                                  {invoice.transactionId || "-"}
                                </span>
                              }
                            />
                            {invoice.rollover && (
                              <>
                                <DetailRow
                                  label="Rolled over from block"
                                  value={
                                    <span className="break-all">
                                      {invoice.rollover.sourceAddBlockId || "-"}
                                    </span>
                                  }
                                />
                                <DetailRow
                                  label="Rollover credits"
                                  value={
                                    typeof invoice.rollover.rolloverAmount ===
                                    "number"
                                      ? invoice.rollover.rolloverAmount.toLocaleString()
                                      : invoice.creditAmount
                                  }
                                />
                                <DetailRow
                                  label="Rolled over on"
                                  value={formatDetailDateTime(
                                    invoice.rollover.createdAt,
                                  )}
                                />
                                <DetailRow
                                  label="Rolled over from cycle"
                                  value={formatCycleRange(
                                    invoice.rollover.previousCycleStart,
                                    invoice.rollover.previousCycleEnd,
                                  )}
                                />
                                <DetailRow
                                  label="Rolled over into cycle"
                                  value={formatCycleRange(
                                    invoice.rollover.currentCycleStart,
                                    invoice.rollover.currentCycleEnd,
                                  )}
                                />
                                <DetailRow
                                  label="Rollover id"
                                  value={
                                    <span className="break-all">
                                      {invoice.rollover.rolloverId || "-"}
                                    </span>
                                  }
                                />
                              </>
                            )}
                          </div>
                          {invoice.meta && (
                            <details className="mt-4">
                              <summary className="cursor-pointer font-['Geist:Medium',_sans-serif] text-[12px] leading-[18px] text-[#605d66]">
                                Raw metadata
                              </summary>
                              <pre className="mt-2 max-h-[240px] overflow-auto whitespace-pre-wrap break-all rounded-xl border border-[#eae7ec] bg-white p-3 font-mono text-[12px] leading-[18px] text-[#605d66]">
                                {JSON.stringify(invoice.meta, null, 2)}
                              </pre>
                            </details>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function CreditUsageTable({
  creditUsageData,
  pagination,
  isLoading,
  onPageChange,
  userNames,
}: {
  creditUsageData: CreditUsageData[];
  pagination: CreditUsagePagination;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  userNames: UserNameMap;
}) {
  if (isLoading && creditUsageData.length === 0) {
    return (
      <div className="text-[14px] text-[#84828e] px-6 py-4">
        Loading credit usage...
      </div>
    );
  }

  if (creditUsageData && creditUsageData.length === 0) {
    return (
      <Frame5
        title="No Credit Usage Found"
        description="Once you start using the service, your credit usage will be tracked here."
      ></Frame5>
    );
  }
  const from = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const to = Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <div
      className="backdrop-blur-[20px] backdrop-filter bg-[rgba(253,252,253,0.88)] border border-[#eae7ec] relative rounded-2xl w-full overflow-hidden"
      data-name="Table"
    >
      <div className="billing-table-scroll max-h-[calc(100vh-280px)] min-h-[280px] w-full overflow-auto">
        <div className="min-w-[1120px]">
          <Table className="w-full table-fixed">
            <TableHeader className="sticky top-0 z-10">
              <TableRow className="border-b-0">
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[170px]">
                  <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]">
                    ID
                  </div>
                </TableHead>
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[220px]">
                  <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]">
                    USER
                  </div>
                </TableHead>
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[190px]">
                  <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]">
                    DATE &amp; TIME
                  </div>
                </TableHead>
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[210px]">
                  <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]">
                    ACTION
                  </div>
                </TableHead>
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[110px]">
                  <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]">
                    TYPE
                  </div>
                </TableHead>
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[120px]">
                  <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]">
                    CREDITS
                  </div>
                </TableHead>
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[140px]">
                  <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]">
                    APP
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {creditUsageData.map((usage: CreditUsageData, index) => (
                <TableRow
                  key={usage.id || index}
                  className="h-[68px] border-b border-[#faf9fb] hover:bg-[#faf9fb]/70"
                >
                  <TableCell className="px-5 py-4">
                    <div
                      className="font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[14px] leading-[18px] truncate"
                      title={usage.id}
                    >
                      {formatUsageId(usage.id)}
                    </div>
                  </TableCell>
                  <TableCell className="px-5 py-4">
                    <div className="min-w-0" title={usage.triggeredByUserId}>
                      <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[14px] leading-[18px] truncate">
                        {getDisplayName(usage.triggeredByUserId, userNames)}
                      </div>
                      {/* The ID beneath a resolved name, for cross-checking. */}
                      {usage.triggeredByUserId &&
                        userNames[usage.triggeredByUserId] && (
                          <div className="truncate text-[12px] leading-[16px] text-[#84828e]">
                            {usage.triggeredByUserId}
                          </div>
                        )}
                    </div>
                  </TableCell>
                  <TableCell className="px-5 py-4">
                    <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[14px] leading-[18px]">
                      {usage.dateTime}
                    </div>
                  </TableCell>
                  <TableCell className="px-5 py-4">
                    <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[14px] leading-[18px] line-clamp-2">
                      {usage.action}
                    </div>
                  </TableCell>
                  <TableCell className="px-5 py-4">
                    <div className="inline-flex h-7 items-center rounded-full bg-[#f2eff3] px-3 font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[13px] leading-[18px]">
                      {usage.type}
                    </div>
                  </TableCell>
                  <TableCell className="px-5 py-4">
                    <div
                      className={`font-['Geist:Medium',_sans-serif] font-medium text-[14px] leading-[18px] ${
                        usage.type === "return"
                          ? "text-[#039855]"
                          : "text-[#d92d20]"
                      }`}
                    >
                      {usage.type === "return" ? "+" : "-"}
                      {usage.credits}
                    </div>
                  </TableCell>
                  <TableCell className="px-5 py-4">
                    <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[14px] leading-[18px] truncate">
                      {usage.studio}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      <div className="sticky bottom-0 z-20 flex flex-wrap items-center justify-between gap-3 border-t border-[#eae7ec] bg-[#faf9fb] px-5 py-3 text-[13px] text-[#5f5a66] shadow-[0_-8px_18px_rgba(33,31,38,0.08)]">
        <div className="font-['Geist:Regular',_sans-serif] leading-[18px]">
          <span className="font-['Geist:Medium',_sans-serif] font-medium text-[#211f26]">
            {from}-{to}
          </span>{" "}
          of {pagination.total} usage records
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            className="h-9 rounded-lg border border-[#d7d3dc] bg-white px-3 font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] disabled:cursor-not-allowed disabled:border-[#eae7ec] disabled:bg-[#f5f3f6] disabled:text-[#a7a3ad]"
            disabled={!pagination.hasPreviousPage || isLoading}
            onClick={() => onPageChange(Math.max(1, pagination.page - 1))}
          >
            Previous
          </button>
          <span className="min-w-[88px] text-center font-['Geist:Regular',_sans-serif] leading-[18px]">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            className="h-9 rounded-lg border border-[#d7d3dc] bg-white px-3 font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] disabled:cursor-not-allowed disabled:border-[#eae7ec] disabled:bg-[#f5f3f6] disabled:text-[#a7a3ad]"
            disabled={!pagination.hasNextPage || isLoading}
            onClick={() => onPageChange(pagination.page + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

function SubscriptionTable({
  subscriptionData,
  workspaceId,
  totalCredits,
  customPlanDetails,
  activeSubscription,
}) {
  console.log("subscriptionData", subscriptionData);
  if (subscriptionData && subscriptionData.length === 0) {
    return (
      <Frame5
        title="No Subscription Data Found"
        description="Once you start using the service, your subscription data will be tracked here."
      ></Frame5>
    );
  }

  const handleCancelPlan = (subscriptionId: string) => {
    if (subscriptionData.length === 1) {
      notify("You need to have at least one active subscription", "error");
      return;
    }

    const confirmCancel = window.confirm(
      "Are you sure you want to cancel the subscription? This action cannot be undone.",
    );
    if (confirmCancel) {
      apiCall(
        `${CENTRALISED_BILLING_API}/subscriptions/cancel-stripe/${workspaceId}/subscription/${subscriptionId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cancelAtPeriodEnd: true }),
        },
      )
        .then((res: any) => {
          if (res && res.message) {
            notify(res.message, "success");
            setTimeout(() => {
              window.location.reload();
            }, 100);
          }
        })
        .catch((err: any) => {
          if (!isCancellation(err)) notify(errorMessage(err), "error");
        });
    }
  };

  return (
    <div
      className="backdrop-blur-[20px] backdrop-filter bg-[rgba(253,252,253,0.88)] border border-[#eae7ec] relative rounded-2xl w-full overflow-hidden"
      data-name="Table"
    >
      <div className="billing-table-scroll max-h-[520px] w-full overflow-auto">
        <div className="min-w-[1120px]">
          <Table className="w-full table-fixed">
            <TableHeader className="sticky top-0 z-10">
              <TableRow className="border-b-0">
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[210px]">
                  <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]">
                    SUBSCRIPTION
                  </div>
                </TableHead>
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[190px]">
                  <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]">
                    PLAN
                  </div>
                </TableHead>
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[130px]">
                  <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]">
                    STATUS
                  </div>
                </TableHead>
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[110px]">
                  <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]">
                    INTERVAL
                  </div>
                </TableHead>
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[140px]">
                  <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]">
                    AMOUNT
                  </div>
                </TableHead>
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[170px]">
                  <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]">
                    NEXT PAYMENT
                  </div>
                </TableHead>
                <TableHead className="bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] w-[170px]">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptionData &&
                subscriptionData.length > 0 &&
                subscriptionData.map((subscription: any) => {
                  const planMetaData = subscription?.plan?.metadata || {};
                  const subscriptionDetails = subscription?.items?.data[0];
                  const nextPaymentDate = subscriptionDetails?.current_period_end
                    ? formatDateToHumanReadable(
                        subscriptionDetails.current_period_end * 1000,
                      )
                    : "-";

                  return (
                    <TableRow
                      key={subscription.id}
                      className="h-[68px] border-b border-[#faf9fb] hover:bg-[#faf9fb]/70"
                    >
                      <TableCell className="px-5 py-4">
                        <div
                          className="font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[14px] leading-[18px] truncate"
                          title={subscription.id}
                        >
                          {formatCompactId(subscription.id)}
                        </div>
                      </TableCell>
                      <TableCell className="px-5 py-4">
                        <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[14px] leading-[18px] truncate">
                          {planMetaData.name || "-"}
                        </div>
                      </TableCell>
                      <TableCell className="px-5 py-4">
                        <span
                          className={`inline-flex h-7 items-center rounded-full px-3 font-['Geist:Medium',_sans-serif] font-medium text-[13px] leading-[18px] ${getStatusPillClass(
                            subscription.status,
                          )}`}
                        >
                          {subscription.status || "-"}
                        </span>
                      </TableCell>
                      <TableCell className="px-5 py-4">
                        <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[14px] leading-[18px] capitalize">
                          {subscription.plan?.interval || "-"}
                        </div>
                      </TableCell>
                      <TableCell className="px-5 py-4">
                        <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[14px] leading-[18px]">
                          {formatBillingAmount(
                            (subscriptionDetails?.price?.unit_amount ?? 0) / 100,
                            subscriptionDetails?.price?.currency ||
                              subscription?.currency ||
                              subscription?.plan?.currency ||
                              subscription?.plan?.metadata?.currency ||
                              getRegisteredBillingCurrency(
                                activeSubscription,
                                customPlanDetails,
                              ),
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="px-5 py-4">
                        <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[14px] leading-[18px]">
                          {nextPaymentDate}
                        </div>
                      </TableCell>
                      <TableCell className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <a
                            href={`${STRIPE_SUBSCRIPTION_PAGE_URL}/${subscription.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex h-9 items-center justify-center rounded-lg border border-[#d7d3dc] bg-white px-3 font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[13px] leading-[18px] transition-colors hover:bg-[#f5f3f6]"
                          >
                            Stripe
                          </a>
                          <button
                            type="button"
                            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-[#fecdca] bg-[#fff5f5] px-3 font-['Geist:Medium',_sans-serif] font-medium text-[#b42318] text-[13px] leading-[18px] transition-colors hover:bg-[#fee4e2]"
                            onClick={() => handleCancelPlan(subscription.id)}
                          >
                            Cancel
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function BillingTable({
  activeTab,
  data,
  workspaceId,
  onTabChange,
  handleUploadInvoiceBtn,
  totalCredits,
  customPlanDetails,
  subscription,
  creditUsagePagination,
  creditUsageLoading,
  onCreditUsagePageChange,
  userNames,
}: {
  activeTab: "invoices" | "creditUsage" | "subscriptions";
  data: InvoiceData[] | CreditUsageData[];
  workspaceId: string;
  onTabChange: (tab: "invoices" | "creditUsage" | "subscriptions") => void;
  handleUploadInvoiceBtn: () => void;
  totalCredits: Number;
  customPlanDetails: any;
  subscription: any;
  creditUsagePagination: CreditUsagePagination;
  creditUsageLoading: boolean;
  onCreditUsagePageChange: (page: number) => void;
  userNames: UserNameMap;
}) {
  return (
    <div
      className="box-border content-stretch flex flex-col gap-4 items-start justify-start p-0 relative rounded-2xl w-full"
      data-name="Table"
    >
      <TabsSection
        activeTab={activeTab}
        onTabChange={onTabChange}
        handleUploadInvoiceBtn={handleUploadInvoiceBtn}
        invoiceTableData={data as InvoiceData[]}
      />
      {activeTab === "invoices" ? (
        <InvoicesTable
          workspaceId={workspaceId}
          invoicesData={data}
          subscription={subscription}
          customPlanDetails={customPlanDetails}
          userNames={userNames}
        />
      ) : activeTab === "subscriptions" ? (
        <SubscriptionTable
          subscriptionData={data}
          totalCredits={totalCredits}
          customPlanDetails={customPlanDetails}
          workspaceId={workspaceId}
          activeSubscription={subscription}
        />
      ) : (
        <CreditUsageTable
          creditUsageData={data as CreditUsageData[]}
          pagination={creditUsagePagination}
          isLoading={creditUsageLoading}
          onPageChange={onCreditUsagePageChange}
          userNames={userNames}
        />
      )}
    </div>
  );
}

function BillingIcon() {
  return (
    <div className="absolute left-3 size-6 top-3" data-name="Billing">
      <svg
        className="block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 24 24"
      >
        <g id="Billing">
          <path
            d={svgPathsBilling.pd838d00}
            fill="var(--fill-0, #FCFCFD)"
            id="Vector"
          />
        </g>
      </svg>
    </div>
  );
}

function Frame5({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="backdrop-blur-md backdrop-filter bg-[rgba(253,252,253,0.64)] relative rounded-2xl  w-full">
      <div className="absolute border border-[#eae7ec] border-solid inset-0 pointer-events-none rounded-2xl" />
      <div className="flex flex-col items-center justify-center relative size-full">
        <div className="box-border content-stretch flex flex-col gap-4 items-center justify-center px-6 py-10 relative w-full">
          <div className="box-border content-stretch flex flex-col gap-4 items-center justify-center p-0 relative  w-80">
            <div
              className="bg-[#2d58ff] relative rounded-[28px]  size-12"
              data-name="Featured icon"
            >
              <div className="absolute border-8 border-[#cadfff] border-solid inset-[-4px] pointer-events-none rounded-[32px]" />
              <BillingIcon />
              <div className="absolute inset-[-4px] pointer-events-none shadow-[0px_1px_1px_1px_inset_rgba(255,255,255,0.12),0px_1px_1px_0px_inset_rgba(255,255,255,0.16)]" />
            </div>{" "}
            <div
              className="box-border content-stretch flex flex-col gap-1 items-start justify-start leading-[0] p-0 relative  text-center w-full"
              data-name="Text and supporting text"
            >
              <div className="font-['Geist:SemiBold',_sans-serif] font-semibold relative  text-[#211f26] text-[18px] tracking-[-0.1px] w-full">
                <p className="block leading-[28px]">{title}</p>
              </div>
              <div className="font-['Geist:Regular',_sans-serif] font-normal relative  text-[#84828e] text-[14px] w-full">
                <p className="block leading-[20px]">{description}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface Plan {
  name: string;
  credits: number;
  price: string;
  features: string[];
  productId?: string;
  priceId?: string;
  currency?: string;
}

export default function Billing() {
  const navigate = useNavigate();
  const params = useParams();
  const globalReducer = useSelector((state: any) => state.globalReducer);

  const [activeTab, setActiveTab] = useState<
    "invoices" | "creditUsage" | "subscriptions"
  >("creditUsage");
  const [availableCredits, setAvailableCredits] = useState<number>(0);
  const [bonusAndTopUpCredits, setBonusAndTopUpCredits] = useState<number>(0);
  const [bonusAndTopUpCreditsBlock, setBonusAndTopUpCreditsBlock] = useState();

  const [subscription, setSubscription] = useState<any>({});
  const [validCredits, setValidCredits] = useState<number>(0);
  const [sharedPool, setSharedPool] = useState<{
    sponsorName: string | null;
    allowance: number;
    /** The workspace's own credits: kept, but paused while it is a member. */
    ownCredits?: number;
  } | null>(null);
  const [invoicesTableData, setInvoicesTableData] = useState<InvoiceData[]>([]);
  const [creditUsageTableData, setCreditUsageTableData] = useState<
    CreditUsageData[]
  >([]);
  const [creditUsagePage, setCreditUsagePage] = useState(1);
  const [creditUsageLoading, setCreditUsageLoading] = useState(false);
  const [creditUsagePagination, setCreditUsagePagination] =
    useState<CreditUsagePagination>({
      page: 1,
      limit: CREDIT_USAGE_PAGE_SIZE,
      total: 0,
      totalPages: 1,
      hasNextPage: false,
      hasPreviousPage: false,
    });
  const [subscriptionsTableData, setSubscriptionsTableData] = useState([]);
  const [userNames, setUserNames] = useState<UserNameMap>({});
  const [showCancelPlan, setShowCancelPlan] = useState(false);

  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [activePlan, setActivePlan] = useState({});
  const [selectedPlan, setSelectedPlan] = useState({});
  const [showCancelDowngradeModal, setShowCancelDowngradeModal] =
    useState(false);

  const [showCustomCreditsModal, setShowCustomCreditsModal] = useState(false);
  const [customPlanDetails, setCustomPlanDetails] = useState({});
  const [workspaceDetails, setWorkspaceDetails] = useState(null);
  const [triggerCreateWorkspaceOnBilling, setTriggerCreateWorkspaceOnBilling] =
    useState(false);

  const [showInvoiceUploadModal, setShowInvoiceUploadModal] = useState(false);
  const [fetchedOfflineInvoices, setFetchedOfflineInvoices] = useState(false);
  const [showBillingDetailsModal, setShowBillingDetailsModal] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);

  useEffect(() => {
    if (params.workspaceId) {
      setCreditUsagePage(1);
      loadUserNames(params.workspaceId);
      fetchAvailableCredits();
      fetchActiveSubscription();
      fetchAllActiveSubscriptions();
      fetchProducts();
      getWorkspaceDetail();
    }
  }, [params.workspaceId]);

  useEffect(() => {
    if (params.workspaceId) {
      fetchInvoicesData();
      fetchCreditUsageData();
      fetchActiveSubscription();
      fetchAllActiveSubscriptions();
    }
  }, [activeTab, params.workspaceId, creditUsagePage]);

  useEffect(() => {
    if (triggerCreateWorkspaceOnBilling && globalReducer.userData) {
      createWorkspaceOnBilling();
    }
  }, [globalReducer.userData, triggerCreateWorkspaceOnBilling]);

  useEffect(() => {
    if (
      invoicesTableData &&
      invoicesTableData.length > 0 &&
      !fetchedOfflineInvoices
    ) {
      const workspaceId = params.workspaceId;
      try {
        const offlineInvoiceRes = fetch(
          `${CENTRALISED_BILLING_API}/invoice/${workspaceId}`,
        );

        offlineInvoiceRes
          .then((res) => res.json())
          .then((offlineInvoiceData) => {
            const invoiceOrderNumbers = offlineInvoiceData.data.map(
              (invoice) => invoice.invoiceId,
            );

            let filteredCustomInvoices = invoicesTableData.map((invoice) => {
              return {
                ...invoice,
                offlineUploaded:
                  invoice.isOfflineInvoice &&
                  invoiceOrderNumbers.indexOf(invoice.orderNumber) > -1
                    ? true
                    : false,
              };
            });

            setFetchedOfflineInvoices(true);
            setInvoicesTableData([...filteredCustomInvoices]);
          });
      } catch (error) {
        console.log(error);
      }
    }
  }, [invoicesTableData, fetchedOfflineInvoices]);

  const uploadedInvoice = () => {
    fetchInvoicesData();
  };

  // Two sources, merged into one map: the workspace's own members, and every
  // user in the org so admin-triggered grants resolve too. Both land
  // asynchronously, so each merges into whatever is already there rather than
  // replacing it.
  const loadUserNames = (workspaceId: string) => {
    fetchWorkspaceUserNames(workspaceId).then((map) =>
      setUserNames((current) => ({ ...current, ...map })),
    );
    fetchAllUserNames().then((map) =>
      setUserNames((current) => ({ ...map, ...current })),
    );
  };

  const fetchAvailableCredits = async () => {
    try {
      const workspaceId = params.workspaceId;
      const response = await apiCall(
        `${CENTRALISED_BILLING_API}/credits/available/${workspaceId}`,
      );

      setAvailableCredits(parseInt(response.availableCredits));
      setSharedPool(response.sharedPool ?? null);

      const bonusAndCreditsCount =
        response.activeBonusAndTopupCreditBlocks &&
        response.activeBonusAndTopupCreditBlocks.reduce(
          (acc, block) => acc + block.amount,
          0,
        );
      setBonusAndTopUpCreditsBlock(response.activeBonusAndTopupCreditBlocks);
      setBonusAndTopUpCredits(bonusAndCreditsCount);
    } catch (error) {
      console.error("Error fetching available credits:", error);
    }
  };

  const createWorkspaceOnBilling = async () => {
    console.log("@createWorkspaceOnBilling");

    if (globalReducer.userData && globalReducer.userData._id) {
      setTriggerCreateWorkspaceOnBilling(false);
      try {
        const triggeredByUserId = globalReducer.userData._id;
        const workspaceId = params.workspaceId;
        const email = globalReducer.userData.email;
        const meta = {
          userEmail: email,
          workspaceId,
          userId: triggeredByUserId,
          serviceNames: [
            "catalogix",
            "brand-eye",
            "dessara",
            "techpack",
            "projects",
            "photogenix",
            "prelogix",
            "catalist",
            "ai-training",
            "insights",
          ],
        };

        console.log("meta", meta);

        const res = await fetch(`${CENTRALISED_BILLING_API}/workspaces`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(meta),
        });
        if (!res.ok)
          throw new Error("Failed to add workspace in billing system");
        else {
          fetchAvailableCredits();
          fetchActiveSubscription();
        }
      } catch (err) {
        console.log(err);
      }
    }
  };

  const fetchActiveSubscription = async () => {
    try {
      setLoading(true);
      const workspaceId = params.workspaceId;
      const response = await apiCall(
        `${CENTRALISED_BILLING_API}/subscriptions/current/${workspaceId}`,
      );
      setLoading(false);

      let currentPlanData = response.data;

      try {
        const customCreditInfoRes = await apiCall(
          `${CENTRALISED_BILLING_API}/custom/` + workspaceId,
        );

        if (customCreditInfoRes && response.data.plan.name == "Custom") {
          currentPlanData = {
            ...currentPlanData,
            credits: customCreditInfoRes.data.credits,
            plan: {
              ...currentPlanData.plan,
              amount: customCreditInfoRes.data.price * 100,
            },
          };
        }
      } catch (error) {
        console.log("Error: ", error);
      }

      setSubscription(currentPlanData);
    } catch (error) {
      console.log("Error", error);
      if (isMissingBillingWorkspace(error)) {
        console.log("register workspace on stripe");
        setTriggerCreateWorkspaceOnBilling(true);
      }
      console.error("Error fetching active subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllActiveSubscriptions = async () => {
    try {
      setLoading(true);
      const workspaceId = params.workspaceId;
      const response = await apiCall(
        `${CENTRALISED_BILLING_API}/subscriptions/active-subscriptions/${workspaceId}`,
      );
      setLoading(false);

      setSubscriptionsTableData(response.data);
    } catch (error) {
      console.log("Error", error);
      if (isMissingBillingWorkspace(error)) {
        console.log("register workspace on stripe");
        setTriggerCreateWorkspaceOnBilling(true);
      }
      console.error("Error fetching active subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCustomCreditInfo = async (workspaceId) => {
    try {
      const response = await apiCall(
        `${CENTRALISED_BILLING_API}/custom/` + workspaceId,
      );
      return response;
    } catch (err) {
      console.log("Error: ", err);
    }
  };

  const getCustomCreditInfoDetails = async () => {
    setShowCustomCreditsModal(false);
    setLoading(true);
    try {
      const workspaceId = params.workspaceId;
      setTimeout(async () => {
        fetchAvailableCredits();
        fetchActiveSubscription();
        fetchProducts();
        fetchInvoicesData();
        fetchCreditUsageData();

        setLoading(false);
      }, 7000);
    } catch (error) {
      setLoading(false);

      console.log("Error: ", error);
    }
  };

  const fetchProducts = async () => {
    const workspaceId = params.workspaceId;

    try {
      const billingCurrency = await getWorkspaceBillingCurrency(workspaceId);
      const response = await apiCall(buildProductsUrl(billingCurrency));
      if (response?.data?.length) {
        let data: Plan[] = response.data
          .map((item) =>
            item.prices.map((priceObj) => {
              const {
                metadata: { name, credits },
                unit_amount: price,
                currency,
              } = priceObj;

              if (item.name.toLowerCase() === "custom") {
                return {
                  name: "Custom",
                  credits: "0",
                  price: formatBillingAmount(Number(price) / 100, currency),
                  features: [],
                  productId: item.id,
                  priceId: priceObj.id,
                  planPriceInFig: Number(price) / 100,
                  currency,
                };
              }

              const features = Object.keys(priceObj.metadata)
                .filter((i) => i.toLowerCase().includes("feature"))
                .map((i) => priceObj.metadata[i]);

              return {
                name: name,
                credits: credits,
                price: formatBillingAmount(Number(price) / 100, currency),
                features,
                productId: item.id,
                priceId: priceObj.id,
                planPriceInFig: Number(price) / 100,
                currency,
              };
            }),
          )
          .flat(1);

        data.sort((a, b) => {
          return a.planPriceInFig! - b.planPriceInFig!;
        });

        data.push({
          name: "Enterprise",
          credits: 999999, // Unlimited represented as large number
          price: "-", // Custom pricing,
          features: [
            "Unlimited image gens/remixes",
            "Unlimited model transformations",
            "Unlimited techpacks generation",
            "Unlimited video generation",
            "Unlimited custom brand scraping",
            "Unlimited product cataloging",
          ],
        });

        try {
          const customCreditInfoRes = await getCustomCreditInfo(workspaceId);

          if (customCreditInfoRes && customCreditInfoRes.data) {
            setCustomPlanDetails(customCreditInfoRes.data);
            data = data.map((plan) => {
              if (plan.name == "Custom") {
                return {
                  ...plan,
                  planPriceInFig: customCreditInfoRes.data.price,
                  credits: customCreditInfoRes.data.credits,
                  price: formatBillingAmount(
                    customCreditInfoRes.data.price,
                    customCreditInfoRes.data.currency,
                  ),
                  currency: customCreditInfoRes.data.currency,
                };
              }
              return plan;
            });
          }
        } catch (error) {
          console.log("Error: ", error);
        }

        setPlans(data);
      }
    } catch (error) {
      setLoading(false);
      console.error("Error fetching products:", error);
    }
  };

  const getWorkspaceDetail = () => {
    getWorkspaceDetails(params.workspaceId)
      .then((res) => {
        setWorkspaceDetails(res.data);
      })
      .catch((err) => {
        console.log("Error: ", err);
      });
  };

  const fetchInvoicesData = async () => {
    try {
      const workspaceId = params.workspaceId;
      const response = await apiCall(
        `${CENTRALISED_BILLING_API}/credits/get-usage-history/${workspaceId}?types=add`,
      );

      // Rollovers are a separate audit collection, keyed to the credit block
      // they created. A failure here must not cost us the invoice table.
      const rolloversByCreditBlockId = new Map<string, any>();
      try {
        const rolloverRes = await getRollOverList(workspaceId);
        (rolloverRes?.data || []).forEach((rollover: any) => {
          if (rollover?.newCreditBlockId) {
            rolloversByCreditBlockId.set(
              String(rollover.newCreditBlockId),
              rollover,
            );
          }
        });
      } catch (error) {
        console.error("Error fetching rollovers:", error);
      }

      const data: InvoiceData[] = response.data.map((item: any) => {
        return {
          creditBlockId: item._id,
          rollover: buildRolloverDetails(
            item.meta,
            rolloversByCreditBlockId.get(String(item._id)),
          ),
          // A credit grant is an audited event, so the row shows when it happened
          // down to the minute rather than just the day.
          date: formatDateToHumanReadable(item.createdAt, true),
          createdAt: item.createdAt,
          orderNumber: item.meta.stripeInvoiceId || item.transactionId,
          plan: formatBillingAmount(
            item.amountPaid,
            item.currency || item.meta?.currency,
          ),
          name: item.amountPaid == 0 ? "free" : "",
          creditAmount: item.amount,
          amountPaid: item.amountPaid,
          currency: item.currency || item.meta?.currency,
          isOfflineInvoice: item.paymentPlatform === "offline" ? true : false,
          meta: item.meta,
          triggeredByUserId: item.triggeredByUserId,
          paymentPlatform: item.paymentPlatform,
          expiresAt: item.expiresAt,
          gracePeriodEndsAt: item.gracePeriodEndsAt,
          transactionId: item.transactionId,
        };
      });

      const today = new Date().toISOString().split("T")[0];

      setValidCredits(
        response.data
          .filter((item) => item.expiresAt.split("T")[0] > today)
          .reduce((total: number, item: any) => total + item.amount, 0),
      );
      setFetchedOfflineInvoices(false);
      setInvoicesTableData(data);
    } catch (error) {
      console.error("Error fetching invoices data:", error);
    }
  };

  const fetchCreditUsageData = async () => {
    try {
      setCreditUsageLoading(true);
      const workspaceId = params.workspaceId;
      const response = await getCreditUsageHistory(workspaceId, {
        types: "deduct,return",
        limit: CREDIT_USAGE_PAGE_SIZE,
        page: creditUsagePage,
      });

      const data: CreditUsageData[] = (response.data || []).map((item: any) => {
        return {
          id: item._id,
          type: item.type || "-",
          triggeredByUserId: item.triggeredByUserId,
          dateTime: formatDateToHumanReadable(item.createdAt, true),
          action: item.meta && item.meta.info ? item.meta.info : "-",
          credits: item.amount,
          studio: item.service?.serviceGroupName || "-",
        };
      });

      setCreditUsageTableData(data);
      setCreditUsagePagination(
        response.pagination || {
          page: creditUsagePage,
          limit: CREDIT_USAGE_PAGE_SIZE,
          total: data.length,
          totalPages: 1,
          hasNextPage: false,
          hasPreviousPage: creditUsagePage > 1,
        },
      );
    } catch (error) {
      console.error("Error fetching credit usage data:", error);
    } finally {
      setCreditUsageLoading(false);
    }
  };

  const handleShowCustomCreditsModal = () => {
    setShowCustomCreditsModal(true);
  };
  const handleShowTopUpModal = () => {
    setShowTopUpModal(true);
  };

  const handleCancelPlan = async () => {
    try {
      setLoading(true);
      // The currency the workspace is registered in is already resolved from
      // the loaded subscription and custom plan; the network lookup only fills
      // in when neither is loaded yet.
      const registeredCurrency =
        resolveRegisteredBillingCurrency(subscription, customPlanDetails) ||
        (await getWorkspaceBillingCurrency(params.workspaceId));
      const response = await apiCall(buildProductsUrl(registeredCurrency));
      if (response?.data?.length) {
        const data: Plan[] = response.data
          .reverse()
          .map((item) =>
            item.prices.map((priceObj) => {
              const {
                metadata: { name, credits },
                unit_amount: price,
                currency,
              } = priceObj;

              const features = Object.keys(priceObj.metadata)
                .filter((i) => i.toLowerCase().includes("feature"))
                .map((i) => priceObj.metadata[i]);

              const planDetail = {
                name: name,
                credits: credits,
                price: formatBillingAmount(Number(price) / 100, currency),
                features,
                productId: item.id,
                priceId: priceObj.id,
                planPriceInFig: Number(price) / 100,
                currency,
              };

              return planDetail;
            }),
          )
          .flat(1);

        // Preselecting a Free plan priced in another currency would submit a
        // priceId Stripe rejects for a currency-locked customer, so no match
        // means no preselection.
        const matchingFreePlan = data.find(
          (plan) =>
            plan.name === "Free" &&
            (!registeredCurrency ||
              String(plan.currency || "").toLowerCase() ===
                String(registeredCurrency).toLowerCase()),
        );

        if (!matchingFreePlan) {
          setLoading(false);
          notify(
            "No Free plan is available in this workspace's billing currency",
            "error",
          );
          return;
        }

        setSelectedPlan(matchingFreePlan);

        data.push({
          name: "Enterprise",
          credits: 999999, // Unlimited represented as large number
          price: "-", // Custom pricing,
          features: [
            "Unlimited image gens/remixes",
            "Unlimited model transformations",
            "Unlimited techpacks generation",
            "Unlimited video generation",
            "Unlimited custom brand scraping",
            "Unlimited product cataloging",
          ],
        });

        const activePlan = data.find((i, idx) => {
          if (i.priceId === subscription.plan.id) {
            return { ...i };
          }
        });

        setActivePlan({ ...activePlan, createdAt: subscription.createdAt });
        setPlans(data);
        setLoading(false);
      }
      setShowCancelPlan(true);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching products:", error);
    }
  };

  const handleCancelDowngrade = async () => {
    try {
      setLoading(true);
      const response = await apiCall(
        `${CENTRALISED_BILLING_API}/subscriptions/revert-downgrade/${params.workspaceId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: globalReducer.userData._id,
          }),
        },
      );
      setLoading(false);
      if (response?.data) {
        console.log(response);
        setShowCancelDowngradeModal(true);
      }
    } catch (error) {
      setLoading(false);
      if (!isCancellation(error)) notify(errorMessage(error), "error");
      console.error("Error :", error);
    }
  };

  const handleChangePlan = async (isCustom: boolean) => {
    setLoading(true);
    try {
      let targetPlan = selectedPlan;

      if (isCustom) {
        const requiredPlan = plans.find((plan) => plan.name === "Custom");
        targetPlan = requiredPlan;
      }

      const triggeredByUserId = globalReducer.userData._id;
      const workspaceId = params.workspaceId;
      const email = globalReducer.userData.email;
      const customMetadata = { ref: "Unified dashboard" };
      const meta = {
        triggeredByUserId,
        workspaceId,
        priceId: targetPlan.priceId,
        productId: targetPlan.productId,
        quantity: 1,
        customerEmail: email || undefined,
        customMetadata,
        successUrl: `${window.location.origin}/workspace/${workspaceId}/billing`,
        cancelUrl: `${window.location.origin}/workspace/${workspaceId}/billing`,
        action: "upgrade",
        userId: triggeredByUserId,
      };

      // const res = await fetch(`${CENTRALISED_BILLING_API}/subscriptions/create`, {
      const res = await fetch(
        `${CENTRALISED_BILLING_API}/subscriptions/update/${workspaceId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(meta),
        },
      );
      console.log(res);
      if (!res.ok) throw new Error("Failed to create checkout session");
      const {
        data: { checkoutUrl },
      } = await res.json();

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        fetchAvailableCredits();
        fetchActiveSubscription();
        fetchAllActiveSubscriptions();
        fetchProducts();
        getWorkspaceDetail();
        // navigate(-1);
      }
      setShowCancelPlan(false);
      setShowCustomCreditsModal(false);
    } catch (error) {
      fetchAvailableCredits();
      fetchActiveSubscription();
      fetchAllActiveSubscriptions();
      fetchProducts();
      getWorkspaceDetail();
      setShowCancelPlan(false);
      setShowCustomCreditsModal(false);

      console.log(error);
      if (!isCancellation(error)) notify(errorMessage(error), "error");
      console.error("Error :", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadInvoiceBtn = () => {
    setShowInvoiceUploadModal(true);
  };

  const handleManageBillingDetails = () => {
    setShowBillingDetailsModal(true);
  };

  if (loading) {
    return "Loading...";
  }

  return (
    <>
      <div
        className="box-border content-stretch flex flex-col gap-8 items-start justify-start relative size-full min-h-full "
        data-name="Billing"
      >
        <div className="flex flex-col font-['Geist:SemiBold',_sans-serif] font-semibold justify-center leading-[0] relative  text-[#211f26] text-[24px] text-left tracking-[-0.24px] w-full">
          <p className="adjustLetterSpacing leading-[30px] whitespace-pre font-semibold text-[18px]">
            Billing - {workspaceDetails?.name}
          </p>
        </div>

        <>
          <StatsCards
            availableCredits={availableCredits}
            bonusAndTopUpCredits={bonusAndTopUpCredits}
            bonusAndTopUpCreditsBlock={bonusAndTopUpCreditsBlock}
            totalValidCredits={validCredits}
            sharedPool={sharedPool}
            subscription={subscription}
            handleShowCustomCreditsModal={handleShowCustomCreditsModal}
            customPlanDetails={customPlanDetails}
            handleCancelDowngrade={handleCancelDowngrade}
            handleCancelPlan={handleCancelPlan}
            handleShowTopUpModal={handleShowTopUpModal}
          />

          {customPlanDetails && customPlanDetails.requestCustomerCardDetails ? (
            <div className="manage-billing-details">
              <div className="manage-btn" onClick={handleManageBillingDetails}>
                Provide card details to move from offline to other payment mode
              </div>
            </div>
          ) : customPlanDetails && customPlanDetails.paymentLink ? (
            <div className="flex items-center gap-[8px] text-[#211f26]">
              <a
                href={customPlanDetails.paymentLink}
                className="text-[#2d58ff] underline payment-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                {customPlanDetails.paymentLink}
              </a>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(customPlanDetails.paymentLink);
                  notify("Link copied to clipboard");
                }}
              >
                Copy
              </button>
            </div>
          ) : null}

          <div className="manage-billing-details">
            <div>Billing Information</div>
            <div className="manage-btn" onClick={handleManageBillingDetails}>
              Manage billing details
            </div>
          </div>

          {/* <div className="manage-billing-details">
            <div>Billing Information</div>
            <div className="manage-btn" onClick={handleManageBillingDetails}>
              Manage billing details
            </div>
          </div> */}

          <BillingTable
            activeTab={activeTab}
            data={
              activeTab === "invoices"
                ? invoicesTableData
                : activeTab === "subscriptions"
                  ? subscriptionsTableData
                  : creditUsageTableData
            }
            workspaceId={params.workspaceId || ""}
            onTabChange={setActiveTab}
            handleUploadInvoiceBtn={handleUploadInvoiceBtn}
            totalCredits={validCredits}
            customPlanDetails={customPlanDetails}
            subscription={subscription}
            creditUsagePagination={creditUsagePagination}
            creditUsageLoading={creditUsageLoading}
            onCreditUsagePageChange={setCreditUsagePage}
            userNames={userNames}
          />
        </>
      </div>
      {showCancelPlan && (
        <Modal
          onClose={() => {
            setShowCancelPlan(false);
          }}
        >
          <ChangeSubscriptionModal
            onClose={() => {
              setShowCancelPlan(false);
            }}
            activePlan={activePlan}
            selectedPlan={selectedPlan}
            handleChangePlan={handleChangePlan}
            customPlanDetails={customPlanDetails}
            subscription={subscription}
          ></ChangeSubscriptionModal>{" "}
        </Modal>
      )}

      {showCancelDowngradeModal && (
        <Modal
          onClose={() => {
            setShowCancelDowngradeModal(false);
          }}
        >
          <CancelDowngrade
            onClose={() => {
              setShowCancelDowngradeModal(false);
              fetchActiveSubscription();
            }}
            subscription={subscription}
          ></CancelDowngrade>
        </Modal>
      )}

      {showCustomCreditsModal && (
        <Modal
          onClose={() => {
            setShowCustomCreditsModal(false);
          }}
          modalStyle={{ width: "100%", height: "100%", right: "0" }}
        >
          <CustomCreditModal
            onClose={() => {
              setShowCustomCreditsModal(false);
            }}
            getCustomCreditInfoDetails={getCustomCreditInfoDetails}
            availableCredits={availableCredits}
            subscription={subscription}
            customPlanDetails={customPlanDetails}
          ></CustomCreditModal>
        </Modal>
      )}

      {showInvoiceUploadModal && (
        <Modal
          onClose={() => {
            setShowInvoiceUploadModal(false);
          }}
          modalStyle={{ width: "100%", height: "100%", right: "0" }}
        >
          <InvoiceUploadModal
            onClose={() => {
              setShowInvoiceUploadModal(false);
            }}
            invoiceTableData={invoicesTableData}
            uploadedInvoice={uploadedInvoice}
          ></InvoiceUploadModal>
        </Modal>
      )}

      {showBillingDetailsModal && (
        <Modal
          onClose={() => {
            setShowBillingDetailsModal(false);
          }}
          modalStyle={{ width: "100%", height: "100%", right: "0" }}
        >
          <BillingDetailsModal
            onClose={() => {
              setShowBillingDetailsModal(false);
            }}
            subscription={subscription}
          ></BillingDetailsModal>
        </Modal>
      )}

      {showTopUpModal && (
        <Modal
          onClose={() => {
            setShowTopUpModal(false);
          }}
          modalStyle={{ width: "100%", height: "100%", right: "0" }}
        >
          <TopupModal
            onClose={() => {
              setShowTopUpModal(false);
            }}
            getCustomCreditInfoDetails={getCustomCreditInfoDetails}
            subscription={subscription}
            customPlanDetails={customPlanDetails}
          ></TopupModal>
        </Modal>
      )}
    </>
  );
}
